// fetchPrivateShowLeaderboard.js
'use strict';
import parseLinkHeader from 'parse-link-header';

import fetch from '../resource/customFetch.js';
import {
  ADD_GOAL,
  ADD_LIST_ITEMS,
  SET_LIST_ITEMS,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';
import getMeData from '../selector/getMeData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import objectifyArrayById from '../resource/objectifyArrayById.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { PRIVATE_SHOW_LEADERBOARD_MINIMUM_USER_AMOUNT } from '../resource/liveStreamConstants.js';
import { SET_LIVE_STREAM_DATA } from '../ActionTypes.js';

/**
 * Fetch private show leaderboard
 * @kind action
 * @param {string} {streamId} - stream id.
 * @param {string} {fundingId} - funding goal id.
 * @param {[]<string>} {userIds} - user ids.
 * @param {number} {limit} - page size.
 * @param {number} {page} - page index.
 * @param {string} {sortBy} - sort by which field.
 * @param {number} {unixTimestamp} - unix timestamp.
 * @return {Promise} Action promise.
 */
const fetchPrivateShowLeaderboard =
  ({
    streamId,
    fundingId,
    userIds = [],
    limit = PRIVATE_SHOW_LEADERBOARD_MINIMUM_USER_AMOUNT,
    page = 1,
    sortBy,
    unixTimestamp,
  }) =>
  async (dispatch, getState) => {
    if (!fundingId || !streamId) {
      return dispatch({ type: '' });
    }

    const timestampOffsetSeconds = getTimestampOffset(
      getState(),
      Accuracy.SECOND
    );

    const timestamp =
      unixTimestamp ||
      getLiveStreamData(
        getState(),
        [streamId],
        'privateShowLeaderboardTimestamp'
      ) ||
      getCurrentUnixTimestamp({
        offsetSeconds: timestampOffsetSeconds,
      });
    if (unixTimestamp) {
      dispatch({
        type: SET_LIVE_STREAM_DATA,
        payload: {
          selectPath: [streamId, 'privateShowLeaderboardTimestamp'],
          data: unixTimestamp,
        },
      });
    }

    const networkingSelectPath = ['goal', fundingId, 'breakdown', page];
    const token = getMeData(getState(), 'token');

    const fetchOptions = {
      method: 'GET',
      headers: {
        ...getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    };
    const url = getResourceUrl({
      endpoint: `/goals/${fundingId}/breakdowns`,
    });
    url.searchParams.append('limit', limit);
    url.searchParams.append('page', page);
    url.searchParams.append('_', timestamp);
    if (sortBy) {
      url.searchParams.append('sort', sortBy);
    }
    if (userIds.length) {
      userIds.forEach(userId => {
        url.searchParams.append('id', userId);
      });
    }
    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });
    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      const data = await response.json();
      // If user ids is not empty, it means user's breakdown request.
      // It's weird to save this breakdown to lists.
      if (!userIds.length) {
        const totalCount = parseInt(response.headers.get('x-total-count'), 10);
        const links = parseLinkHeader(response.headers.get('Link'));
        const nextPage = links && links.next && parseInt(links.next.page, 10);
        const lastPage = links && links.last && parseInt(links?.last?.page, 10);
        dispatch({
          type: page === 1 ? SET_LIST_ITEMS : ADD_LIST_ITEMS,
          payload: {
            selectPath: ['goal', fundingId, 'breakdown'],
            itemIds: data.map(item => item.id),
            totalCount,
            nextPage,
            lastPage,
          },
        });
      }
      dispatch({
        type: ADD_GOAL,
        payload: {
          id: fundingId,
          breakdown: {
            ...objectifyArrayById({
              array: data,
            }),
          },
        },
      });
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

export default fetchPrivateShowLeaderboard;
